import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('avaluam_token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export interface Cicle {
  id: number;
  nom: string;
  descripcio: string;
  actiu: boolean;
  nombre_cursos: number;
}

export const ciclesService = {
  getAll: async (): Promise<Cicle[]> => {
    const response = await api.get('/cicles');
    return response.data;
  },

  getById: async (id: number): Promise<Cicle> => {
    const response = await api.get(`/cicles/${id}`);
    return response.data;
  },

  create: async (data: { nom: string; descripcio: string; actiu: boolean; nombre_cursos: number }): Promise<Cicle> => {
    const response = await api.post('/cicles', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Cicle>): Promise<Cicle> => {
    const response = await api.put(`/cicles/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/cicles/${id}`);
  },

  reorder: async (cicles: Cicle[]): Promise<void> => {
    await api.put('/cicles/reorder', { cicles });
  },
};

export interface Modul {
  id: number;
  cicle_id?: number;
  codi: string;
  nom: string;
  hores: number;
  curs: number;
  ordre?: number;
}

export const modulsService = {
  getAll: async (cicleId: number): Promise<Modul[]> => {
    const response = await api.get(`/cicles/${cicleId}/moduls`);
    return response.data;
  },

  getById: async (cicleId: number, id: number): Promise<Modul> => {
    const response = await api.get(`/cicles/${cicleId}/moduls/${id}`);
    return response.data;
  },

  create: async (cicleId: number, data: { codi: string; nom: string; hores: number; curs: number }): Promise<Modul> => {
    const response = await api.post(`/cicles/${cicleId}/moduls`, data);
    return response.data;
  },

  update: async (cicleId: number, id: number, data: Partial<Modul>): Promise<Modul> => {
    const response = await api.put(`/cicles/${cicleId}/moduls/${id}`, data);
    return response.data;
  },

  delete: async (cicleId: number, id: number): Promise<void> => {
    await api.delete(`/cicles/${cicleId}/moduls/${id}`);
  },

  reorder: async (cicleId: number, moduls: Modul[]): Promise<void> => {
    await api.put(`/cicles/${cicleId}/moduls/reorder`, { moduls });
  },
};

export interface ResultatAprenentatge {
  id: number;
  modul_id: number;
  codi: string;
  nom: string;
  descripcio: string | null;
  hores: number | null;
  continguts: string | null;
  criteris_avaluacio: string | null;
  ordre: number;
}

export type ResultatAprenentatgeForm = {
  codi: string;
  nom: string;
  descripcio: string;
  hores: number | null;
  continguts: string;
  criteris_avaluacio: string;
};

export const resultatsAprenentatgeService = {
  getAll: async (cicleId: number, modulId: number): Promise<ResultatAprenentatge[]> => {
    const response = await api.get(`/cicles/${cicleId}/moduls/${modulId}/ra`);
    return response.data;
  },

  create: async (
    cicleId: number,
    modulId: number,
    data: ResultatAprenentatgeForm
  ): Promise<ResultatAprenentatge> => {
    const response = await api.post(`/cicles/${cicleId}/moduls/${modulId}/ra`, data);
    return response.data;
  },

  update: async (
    cicleId: number,
    modulId: number,
    id: number,
    data: ResultatAprenentatgeForm
  ): Promise<ResultatAprenentatge> => {
    const response = await api.put(`/cicles/${cicleId}/moduls/${modulId}/ra/${id}`, data);
    return response.data;
  },

  delete: async (cicleId: number, modulId: number, id: number): Promise<void> => {
    await api.delete(`/cicles/${cicleId}/moduls/${modulId}/ra/${id}`);
  },

  reorder: async (
    cicleId: number,
    modulId: number,
    resultats: ResultatAprenentatge[]
  ): Promise<void> => {
    await api.put(`/cicles/${cicleId}/moduls/${modulId}/ra/reorder`, { resultats });
  },
};

export interface Rol {
  id: number;
  codi: string;
  nom: string;
  categoria: 'perfil' | 'carrec';
  ambit: 'global' | 'professorat' | 'alumnat';
  descripcio: string | null;
}

export interface Usuari {
  id: number;
  email: string;
  nom: string;
  cognoms: string;
  telefon: string | null;
  actiu: boolean;
  ultim_acces_at: string | null;
  created_at: string;
  updated_at: string;
  rols: Rol[];
}

export interface UsuariForm {
  email: string;
  password?: string;
  nom: string;
  cognoms: string;
  telefon: string;
  actiu: boolean;
  rol_ids: number[];
}

export interface LoginResponse {
  token: string;
  usuari: Usuari;
}

export const authService = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  me: async (): Promise<Usuari> => {
    const response = await api.get('/auth/me');
    return response.data.usuari;
  },
};

export const rolsService = {
  getAll: async (): Promise<Rol[]> => {
    const response = await api.get('/rols');
    return response.data;
  },
};

export const usuarisService = {
  getAll: async (): Promise<Usuari[]> => {
    const response = await api.get('/usuaris');
    return response.data;
  },

  getById: async (id: number): Promise<Usuari> => {
    const response = await api.get(`/usuaris/${id}`);
    return response.data;
  },

  create: async (data: UsuariForm): Promise<Usuari> => {
    const response = await api.post('/usuaris', data);
    return response.data;
  },

  update: async (id: number, data: UsuariForm): Promise<Usuari> => {
    const response = await api.put(`/usuaris/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/usuaris/${id}`);
  },
};

export default api;
